export const MAX_DIFF_RATIO = 0.01;

// @ts-ignore - TS1501 TypeScript 5.9.2 upgrade
const TRAILING_NEWLINES_REGEX = /\n+$/u;

/**
 * Calculate the difference ratio between two markdown strings.
 * @param {{ input: string; output: string }} root0 - An object containing the input and output markdown strings.
 * @param {string} root0.input - The input markdown string to compare against the output.
 * @param {string} root0.output - The output markdown string to compare against the input.
 * @returns The difference ratio as a number between 0 and 1, where 0 means no difference and 1 means completely different.
 *
 * @example
 * ```js
 * const ratio = calculateDiffRatio({ input: "Hello", output: "Hello" });
 * console.log(ratio); // 0 (no difference)
 * ```
 */
export const calculateDiffRatio = ({
	input,
	output,
}: {
	input: string;
	output: string;
}): number => {
	// Remove leading and trailing whitespace from both input and output
	input = input.trim();
	output = output.trim();

	// Remove Zero Width Non-Joiner
	// @ts-ignore - TS1501 TypeScript 5.9.2 upgrade
	input = input.replace(/\u200C/gu, '');
	// @ts-ignore - TS1501 TypeScript 5.9.2 upgrade
	output = output.replace(/\u200C/gu, '');

	// Remove \n from the end of input and output
	input = input.replace(TRAILING_NEWLINES_REGEX, '');
	output = output.replace(TRAILING_NEWLINES_REGEX, '');

	// If both input and output are empty, return 0 (no difference)
	if (input === '' && output === '') {
		return 0;
	}
	// If either input or output is empty, return 1 (completely different)
	if (input === '' || output === '') {
		return 1;
	}

	/**
	 * Bail out early if the input and output length are so different that even
	 * the longest common subsequence cannot produce a diff ratio less than MAX_DIFF_RATIO
	 * MAX_DIFF_RATIO > 1 - (2 * a) / (a + b)
	 * b > (1 + MAX_DIFF_RATIO) / (1 - MAX_DIFF_RATIO) * a
	 */
	const longStringLength = Math.max(input.length, output.length);
	const shortStringLength = Math.min(input.length, output.length);
	if (longStringLength > ((1 + MAX_DIFF_RATIO) / (1 - MAX_DIFF_RATIO)) * shortStringLength) {
		return 1;
	}

	// If the string are exactly the same, return 0 (no difference)
	if (input === output) {
		return 0;
	}

	/**
	 * Bail out of calculating the diff ratio if the LCS cannot produce a diff ratio less than MAX_DIFF_RATIO
	 * MAX_DIFF_RATIO > 1 - (2 * lcs) / (a + b)
	 * This is a performance optimization to avoid unnecessary calculations
	 * when the strings are too different.
	 */

	const minLcsLength = ((1 - MAX_DIFF_RATIO) / 2) * (input.length + output.length);

	let isMinLcsPossible = true;

	// Calculate the length of the longest common subsequence (LCS)
	const lcsLength = (a: string, b: string): number => {
		const dp: number[][] = Array.from({ length: a.length + 1 }, () => Array(b.length + 1).fill(0));

		// Remaining maximum possible LCS at any point
		for (let i = 1; i <= a.length; i++) {
			let maxRowLcs = 0; // Tracks the maximum LCS in the current row
			for (let j = 1; j <= b.length; j++) {
				if (a[i - 1] === b[j - 1]) {
					dp[i][j] = dp[i - 1][j - 1] + 1;
				} else {
					dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
				}

				// Update the maximum LCS found in this row
				maxRowLcs = Math.max(maxRowLcs, dp[i][j]);
			}

			// Estimate the maximum possible LCS length from this point
			const remainingLengthA = a.length - i; // Remaining characters in `a`
			const remainingLengthB = b.length; // All characters in `b` might still match
			const maxPossibleLcs = maxRowLcs + Math.min(remainingLengthA, remainingLengthB);

			// If the maximum possible LCS is less than the required threshold
			if (maxPossibleLcs < minLcsLength) {
				// Update the flag to indicate bailout
				isMinLcsPossible = false;
				// No need to continue further, we can return 0
				return 0;
			}
		}
		return dp[a.length][b.length];
	};

	const lcs = lcsLength(input, output);

	if (!isMinLcsPossible) {
		return 1;
	}

	// Calculate the difference ratio
	const diffRatio = 1 - (2 * lcs) / (input.length + output.length);
	return Math.max(0, Math.min(1, diffRatio)); // Ensure the ratio is between 0 and 1
};
